import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { formatDistanceToNow } from 'date-fns'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'
import { Spinner } from '../components/ui/Spinner'

const STATUS_BADGE = {
  pending:    'bg-amber-100 text-amber-800',
  processing: 'bg-blue-100 text-blue-800',
  complete:   'bg-emerald-100 text-emerald-800',
  failed:     'bg-rose-100 text-rose-800',
}

export default function DataExport() {
  const { user, household, isAdmin } = useAuth()
  const [scope, setScope] = useState('personal')
  const [format, setFormat] = useState('json')
  const [requesting, setRequesting] = useState(false)
  const [requestError, setRequestError] = useState('')

  const { data: exports = [], refetch } = useQuery({
    queryKey: ['export-requests', user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('export_requests')
        .select('*')
        .eq('requested_by', user.id)
        .order('created_at', { ascending: false })
        .limit(10)
      if (error) throw error
      return data ?? []
    },
    refetchInterval: (data) => {
      const hasPending = data?.some((e) => ['pending', 'processing'].includes(e.status))
      return hasPending ? 5000 : false
    },
  })

  const handleRequest = async () => {
    setRequesting(true)
    setRequestError('')
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/export-data`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({ scope, format }),
        }
      )
      const json = await res.json()
      if (!res.ok) throw new Error(json.error ?? 'Request failed')
      refetch()
    } catch (err) {
      setRequestError(err.message)
    } finally {
      setRequesting(false)
    }
  }

  const getDownloadUrl = async (filePath) => {
    const { data } = await supabase.storage
      .from('household-documents')
      .createSignedUrl(filePath, 300)
    if (data?.signedUrl) window.open(data.signedUrl, '_blank')
  }

  return (
    <div className="max-w-lg mx-auto px-4 py-6">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/settings" className="text-text-secondary hover:text-text-primary">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </Link>
        <h1 className="text-xl font-bold text-text-primary">Data Export</h1>
      </div>

      <section className="bg-surface rounded-base border border-border p-5 mb-6">
        <h2 className="text-sm font-semibold text-text-primary mb-4">Request new export</h2>

        <div className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-text-muted uppercase tracking-wide mb-2">Scope</label>
            <div className="grid grid-cols-2 gap-2">
              {['personal', 'household'].map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setScope(s)}
                  disabled={s === 'household' && !isAdmin}
                  className={`py-2.5 px-4 rounded-base border text-sm font-medium transition-colors ${
                    scope === s
                      ? 'bg-primary text-white border-primary'
                      : 'border-border text-text-secondary hover:bg-background'
                  } disabled:opacity-40 disabled:cursor-not-allowed`}
                >
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                  {s === 'household' && !isAdmin && (
                    <span className="block text-xs font-normal opacity-70">Admin only</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-text-muted uppercase tracking-wide mb-2">Format</label>
            <div className="grid grid-cols-2 gap-2">
              {['json', 'csv'].map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFormat(f)}
                  className={`py-2.5 px-4 rounded-base border text-sm font-medium transition-colors ${
                    format === f
                      ? 'bg-primary text-white border-primary'
                      : 'border-border text-text-secondary hover:bg-background'
                  }`}
                >
                  {f.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          {requestError && (
            <p className="text-sm text-rose-600">{requestError}</p>
          )}

          <button
            type="button"
            onClick={handleRequest}
            disabled={requesting}
            className="w-full bg-primary text-white rounded-base py-3 text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {requesting ? <><Spinner size="sm" /> Requesting…</> : 'Request Export'}
          </button>
          <p className="text-xs text-text-muted text-center">
            Limited to 3 exports per 24 hours. You'll receive an email with the download link.
          </p>
        </div>
      </section>

      {exports.length > 0 && (
        <section>
          <h2 className="text-sm font-semibold text-text-secondary uppercase tracking-wide mb-3">Export history</h2>
          <div className="bg-surface rounded-base border border-border overflow-hidden">
            {exports.map((exp, i) => (
              <div
                key={exp.id}
                className={`flex items-center justify-between px-5 py-4 ${
                  i < exports.length - 1 ? 'border-b border-border' : ''
                }`}
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-text-primary capitalize">
                    {exp.scope} · {exp.format.toUpperCase()}
                  </p>
                  <p className="text-xs text-text-muted mt-0.5">
                    {formatDistanceToNow(new Date(exp.created_at), { addSuffix: true })}
                  </p>
                </div>
                <div className="flex items-center gap-3 ml-4">
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_BADGE[exp.status]}`}>
                    {exp.status}
                  </span>
                  {exp.status === 'complete' && exp.file_path && (
                    <button
                      type="button"
                      onClick={() => getDownloadUrl(exp.file_path)}
                      className="text-primary text-xs font-semibold hover:underline"
                    >
                      Download
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  )
}
